import { useState, type FormEvent } from "react";

interface DiscountCodeProps {
  appliedCode: string | null;
  discount: number;
  isApplying: boolean;
  error: string | null;
  onApply: (code: string) => void;
  onRemove: () => void;
}

export default function DiscountCode({
  appliedCode,
  discount,
  isApplying,
  error,
  onApply,
  onRemove,
}: DiscountCodeProps) {
  const [code, setCode] = useState("");
  const trimmedCode = code.trim();

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!trimmedCode) return;
    onApply(trimmedCode);
  };

  if (appliedCode) {
    return (
      <div className="flex items-center justify-between gap-4 rounded-xl border border-green-300 bg-green-50 p-4">
        <div className="flex flex-col gap-1 min-w-0">
          <p className="text-sm font-medium text-green-700 break-all">
            Code Applied: {appliedCode}
          </p>
          <p className="text-sm text-green-600">
            You saved ${discount.toFixed(2)} on this order.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setCode("");
            onRemove();
          }}
          className="shrink-0 text-sm font-medium text-green-700 underline hover:text-green-800 cursor-pointer"
        >
          Remove
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
      <label
        htmlFor="discountCode"
        className="block text-sm/6 font-normal text-gray-950"
      >
        Discount Code
      </label>
      <div className="flex gap-3">
        <input
          id="discountCode"
          type="text"
          autoComplete="off"
          placeholder="Enter discount code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="block w-full min-w-0 rounded-xl border border-gray-200 px-4 py-3.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500"
        />
        <button
          type="submit"
          disabled={!trimmedCode || isApplying}
          className="shrink-0 rounded-xl border border-gray-200 px-5 text-sm font-medium text-gray-950 hover:bg-gray-50 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isApplying ? "Applying..." : "Apply"}
        </button>
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
    </form>
  );
}
